import { format, set } from "date-fns";
import getSymbolFromCurrency from "currency-symbol-map";
import numeral from "numeral";
import { intersection } from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useState } from "react";
import { MembershipsType } from "redux/services/membership/types";
import type { WalletDto } from "redux/services/wallet/types";
import { getCode, getName } from "country-list";
import { GetDocumentResponse } from "redux/services/documentRepository/types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const FORMATS = {
  AMOUNT: "0,0.00",
  DATE: "yyyy-MM-dd",
  DATE_TIME: "yyyy-MM-dd H:m:s",
  DATE_SLASH: "yyyy/MM/dd",
  "YYYY/MM/DD": "YYYY/MM/DD",
  "YYYY-MM-DD": "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss": "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DD HH:mm": "YYYY-MM-DD HH:mm",
};

export const getCurrentDate = (dateFormat = FORMATS.DATE) =>
  format(new Date(), dateFormat);

export const getCurrentDateTime = (formatType?: string) =>
  format(new Date(), formatType || FORMATS.DATE_TIME);

export const getCurrentMonthFirstDate = () =>
  format(set(new Date(), { date: 1 }), FORMATS.DATE);

export const formatMoney = (amount = 0, currency = "PHP") => {
  const currencyCode = getSymbolFromCurrency(currency);
  if (amount > 0) {
    return `${currencyCode}${numeral(amount).format(FORMATS.AMOUNT)}`;
  } else {
    return `-${currencyCode}${numeral(Math.abs(amount)).format(
      FORMATS.AMOUNT
    )}`;
  }
};

export const formatInputAmount = (value: string) => {
  return `${numeral(value).format("0,0.00")}`;
};

export const constructFullName = (
  firstName?: string,
  middleName?: string,
  lastName?: string
) => [firstName, middleName, lastName].filter((n) => !!n).join(" ");

export const isPermitted = (userRoles: string[], requiredRoles: string[]) => {
  if (requiredRoles.length === 0) return true;
  return intersection(userRoles, requiredRoles).length > 0;
};

export const dateFormat = (
  val?: string,
  useLocalTimezone = false,
  format = "YYYY-MM-DD hh:mm A"
) => {
  if (!val) return undefined;
    return dayjs(val).format(format);

};

export const numberWithCommas = (x?: number) => {
  if (!x || x === 0) return "0";
  return numeral(x).format("0,0");
};

export const downloadFileURLWithCustomName = (
  url: string,
  documentName: string
) => {
  fetch(url)
    .then((res) => {
      return res.blob();
    })
    .then((blob) => {
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = documentName;
      a.href = href;
      a.click();
      a.href = "";
    })
    .catch((err) => console.error(err));
};

export const getRowIndex = (
  pageNumber: number,
  pageSize: number,
  index: number
) => {
  return pageSize * (pageNumber - 1) + index + 1;
};

export const useDebounce = (
  value?: string,
  delay?: number
): string | undefined => {
  const [debouncedValue, setDebouncedValue] = useState<string | undefined>(
    value
  );

  useEffect(() => {
    const handleDebounce = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return (): void => {
      clearTimeout(handleDebounce);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const objectToValueUrlEncoded = (obj: { [key: string]: any }) => {
  let str = [];
  for (let p in obj)
    if (obj.hasOwnProperty(p) && !!obj[p]) {
      str.push(`"${p}":"${obj[p]}"`);
    }
  return str.join("&");
};

export const renderContentTypeAsExtensionFile = (filename: string) => {
  const ext = filename.split(".").pop();

  switch (ext) {
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    default:
      return "";
  }
};

export const getOrganisationMembership = (
  data?: MembershipsType[]
): MembershipsType | undefined => {
  return data?.find((item) => item.organisationName && item.organisationNumber);
};

export const getWalletNumber = (bankAccount: WalletDto["bankAccount"]) => {
  return `${bankAccount.bankBranchId.replaceAll("-", "")}${
    bankAccount.accountNumber
  }`;
};

export const getTransactionRef = () => {
  const date = getCurrentDate();
  const array = new Uint32Array(1);

  const rand = typeof globalThis.crypto?.getRandomValues === "function"
    ? globalThis.crypto.getRandomValues(array)[0]
    : Math.floor(Math.random() * 1000000);// NOSONAR: This is not security-critical; fallback for non-crypto environments

  return `DIGI-${date}-${rand}`;
};


export const parseJwt = (token: string) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
};

export const getNameByTPPPKey = (key: string | undefined) => {
  if (key === undefined || key === "") return "ALL";
  return key;
};

// Country list
// Key: Expected country name, Value: Library country name
const countryMapping: Record<string, string> = {
  "United Kingdom": "United Kingdom of Great Britain and Northern Ireland",
  "United States": "United States of America",
  "Aland Islands": "Åland Islands",
  "Vietnam": "Viet Nam",
};

export const getFixedName = (countryCode: string): string => {
  const mappedName = Object.entries(countryMapping).find(
    ([shortName, fullName]) => getCode(fullName) === countryCode
  )?.[0];
  return mappedName || getName(countryCode) || countryCode;
};

export const getFixedCode = (country: string): string => {
  const fixedCountry = countryMapping[country] || country;
  return getCode(fixedCountry) || "";
};


export function downloadCSVFileFromBase64(documentResponse: GetDocumentResponse) {
  try {
    // Validate input
    if (!documentResponse?.data?.content || !documentResponse?.data?.name) {
      throw new Error('Invalid JSON data: Missing content or name');
    }

    // Extract the base64-encoded content and file name
    const base64Content = documentResponse.data.content;
    const fileName = documentResponse.data.name ?? 'download.csv';

    // Decode base64 content
    let decodedContent;
    try {
      decodedContent = atob(base64Content);
    } catch (err) {
      if (err instanceof Error) {
        throw new Error('Failed to decode base64 content: ' + err.message);
      } else {
        throw new Error('Failed to decode base64 content: Unknown error');
      }
    }

    // Verify decoded content is not empty
    if (!decodedContent) {
      throw new Error('Decoded content is empty');
    }

    // Create a Blob for the CSV content
    const blob = new Blob([decodedContent], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    // Append to DOM, trigger download, and clean up
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV file:', error);
    if (error instanceof Error) {
      alert('Failed to download the file: ' + error.message);
    } else {
      alert('Failed to download the file: An unknown error occurred');
    }
  }
}